/**
 * clientAddressesApi — transport + hooks for client_addresses (0137,
 * WS-4 D2 — the "currently at" model).
 *
 *   PUT  /api/t/{slug}/v1/clients/{id}/addresses                    → { addresses, geocode_outcome }
 *   POST /api/t/{slug}/v1/clients/{id}/addresses/{addrId}/archive   → { addresses }
 *   POST /api/t/{slug}/v1/clients/{id}/addresses/{addrId}/restore   → { addresses }
 *   POST /api/t/{slug}/v1/address-check                             → { outcome, suggestion }
 *
 * The record page DISPLAYS addresses from the aggregate snapshot
 * (record.addresses). A save, archive or restore invalidates the record
 * (one audited re-read, the RowEditDialog posture), the client map, the
 * map prospects and every Assign step suggestion, since each reads the
 * client's addresses. A 409 (a duplicate, or a write that raced another)
 * re-reads the record too, so the card and the band show what is stored.
 * Server gate: manage_client_identity on the writes.
 *
 * The address check (checkClientAddress) runs before a save whose text
 * changed. It never blocks a save: a non-2xx answer, a failed or
 * abandoned request, or a body the dialogs cannot act on
 * (parseAddressCheck) all read as 'unavailable', never as a save error.
 * The browser gives up after ADDRESS_CHECK_TIMEOUT_MS through its own
 * AbortController, so fake timers in the tests own the wait.
 */

import { writeFetch } from './writeFetch';
import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { ApiError } from './api';
import { clientRecordKey } from './clientRecordApi';
import { assignmentSuggestionsPrefix, clientMapKey, mapProspectsKey } from './capacityApi';

export interface ClientAddressInput {
  /** Present = edit that row; absent/undefined = create. */
  id?: string;
  label: string;
  line1: string;
  line2: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  is_current_location: boolean;
  is_primary: boolean;
  /** 0225: the type of place, since when, the caregiving at a home. On an
   *  edit an absent field is unchanged and a null clears it. */
  place_type?: string | null;
  place_since?: string | null;
  /** 0241: the facility or place name; absent = unchanged on an edit. */
  place_name?: string | null;
}

export interface ClientAddressWire {
  id: string;
  label: string;
  line1: string;
  line2: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  is_current_location: boolean;
  is_primary: boolean;
  is_active: boolean;
  updated_at: string;
}

/** What an address save did to the map (the PUT's geocode_outcome). */
export type ClientAddressGeocodeOutcome = 'placed' | 'approximate' | 'no_match' | 'unavailable' | 'not_attempted';

const GEOCODE_OUTCOMES: readonly ClientAddressGeocodeOutcome[] = [
  'placed',
  'approximate',
  'no_match',
  'unavailable',
  'not_attempted',
];

/** A missing or unrecognised outcome reads as not_attempted: nothing to say. */
export function parseGeocodeOutcome(raw: unknown): ClientAddressGeocodeOutcome {
  return GEOCODE_OUTCOMES.find((o) => o === raw) ?? 'not_attempted';
}

export interface ClientAddressSaveWire {
  addresses: ClientAddressWire[];
  geocode_outcome: ClientAddressGeocodeOutcome;
}

async function parseOrThrow<T>(res: Response, label: string): Promise<T> {
  if (!res.ok) {
    let body: unknown;
    try {
      body = await res.json();
    } catch {
      body = await res.text().catch(() => null);
    }
    throw new ApiError(res.status, body, `${label} → HTTP ${res.status}`);
  }
  return (await res.json()) as T;
}

async function putAddress(
  slug: string,
  clientId: string,
  input: ClientAddressInput,
): Promise<ClientAddressSaveWire> {
  const url = `/api/t/${encodeURIComponent(slug)}/v1/clients/${encodeURIComponent(clientId)}/addresses`;
  const res = await writeFetch(url, {
    method: 'PUT',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  const payload = await parseOrThrow<{ addresses: ClientAddressWire[]; geocode_outcome?: unknown }>(res, `PUT ${url}`);
  return { addresses: payload.addresses, geocode_outcome: parseGeocodeOutcome(payload.geocode_outcome) };
}

async function postAddressLifecycle(
  slug: string,
  clientId: string,
  addressId: string,
  archived: boolean,
): Promise<ClientAddressWire[]> {
  const url = `/api/t/${encodeURIComponent(slug)}/v1/clients/${encodeURIComponent(clientId)}/addresses/${encodeURIComponent(addressId)}/${archived ? 'archive' : 'restore'}`;
  // The route reads no body; an empty JSON object keeps every POST this
  // module sends the same shape.
  const res = await writeFetch(url, {
    method: 'POST',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: '{}',
  });
  const payload = await parseOrThrow<{ addresses: ClientAddressWire[] }>(res, `POST ${url}`);
  return payload.addresses;
}

/** Every read that shows a client's addresses: the record, the client
 *  map, the map prospects and the Assign step's suggestions. */
function invalidateAddressReads(qc: QueryClient, slug: string, clientId: string): void {
  void qc.invalidateQueries({ queryKey: clientRecordKey(slug, clientId) });
  void qc.invalidateQueries({ queryKey: clientMapKey(slug) });
  void qc.invalidateQueries({ queryKey: mapProspectsKey(slug) });
  void qc.invalidateQueries({ queryKey: assignmentSuggestionsPrefix(slug) });
}

/** A 409 means the stored addresses are not what the form showed: re-read them. */
function rereadOnConflict(qc: QueryClient, slug: string, clientId: string, err: unknown): void {
  if (err instanceof ApiError && err.status === 409) {
    void qc.invalidateQueries({ queryKey: clientRecordKey(slug, clientId) });
  }
}

/** Create or edit one address (primary/current swaps are server-atomic). */
export function useSaveClientAddress(slug: string, clientId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ClientAddressInput) => putAddress(slug, clientId, input),
    onSuccess: () => invalidateAddressReads(qc, slug, clientId),
    onError: (err) => rereadOnConflict(qc, slug, clientId, err),
  });
}

/** Soft-archive / restore one address (archiving clears both flags). */
export function useSetClientAddressArchived(slug: string, clientId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ addressId, archived }: { addressId: string; archived: boolean }) =>
      postAddressLifecycle(slug, clientId, addressId, archived),
    onSuccess: () => invalidateAddressReads(qc, slug, clientId),
    onError: (err) => rereadOnConflict(qc, slug, clientId, err),
  });
}

// ---------------------------------------------------------------------
// The address check before a save
// ---------------------------------------------------------------------

/** How long the browser waits for the check before it saves anyway. */
export const ADDRESS_CHECK_TIMEOUT_MS = 8000;

/** The five postal fields the check reads, and a suggestion's shape. */
export interface AddressCheckFields {
  line1: string;
  line2: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
}

export type AddressCheckOutcome = 'matches' | 'differs' | 'not_found' | 'unavailable';

export interface AddressCheckResult {
  outcome: AddressCheckOutcome;
  /** Non-null exactly for differs. */
  suggestion: AddressCheckFields | null;
}

const UNAVAILABLE: AddressCheckResult = { outcome: 'unavailable', suggestion: null };

function nullableText(v: unknown): v is string | null {
  return v === null || typeof v === 'string';
}

/** A parsed 2xx body as something the dialogs can act on; anything else
 *  (an unknown outcome, a missing body, differs without a suggestion
 *  whose line1 is a string) reads as unavailable. */
export function parseAddressCheck(body: unknown): AddressCheckResult {
  if (typeof body !== 'object' || body === null) return UNAVAILABLE;
  const { outcome, suggestion } = body as { outcome?: unknown; suggestion?: unknown };
  if (outcome === 'matches' || outcome === 'not_found') return { outcome, suggestion: null };
  if (outcome !== 'differs') return UNAVAILABLE;
  if (typeof suggestion !== 'object' || suggestion === null) return UNAVAILABLE;
  const s = suggestion as Record<string, unknown>;
  if (typeof s['line1'] !== 'string') return UNAVAILABLE;
  const line2 = s['line2'] ?? null;
  const city = s['city'] ?? null;
  const state = s['state'] ?? null;
  const zip = s['zip'] ?? null;
  if (!nullableText(line2) || !nullableText(city) || !nullableText(state) || !nullableText(zip)) return UNAVAILABLE;
  return { outcome: 'differs', suggestion: { line1: s['line1'], line2, city, state, zip } };
}

/** Ask the server whether Azure Maps finds the typed address. Never
 *  throws: a non-2xx answer, a failed fetch or the browser's own timeout
 *  answers unavailable. */
export async function checkClientAddress(slug: string, fields: AddressCheckFields): Promise<AddressCheckResult> {
  const url = `/api/t/${encodeURIComponent(slug)}/v1/address-check`;
  const ctl = new AbortController();
  const timer = setTimeout(() => ctl.abort(), ADDRESS_CHECK_TIMEOUT_MS);
  try {
    const res = await writeFetch(url, {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify(fields),
      signal: ctl.signal,
    });
    if (!res.ok) return UNAVAILABLE;
    return parseAddressCheck(await res.json());
  } catch {
    return UNAVAILABLE;
  } finally {
    clearTimeout(timer);
  }
}
